import { ErrorApp, traducirErrorSupabase } from '../../domain/errores';
import {
  interpretarEsquema,
  interpretarValoresBase,
  type CampoConfiguracion,
} from '../../domain/value-objects/EsquemaConfiguracion';
import { aBooleano, aNumero, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';
import { ambitoSesion } from './AmbitoSesion';
import type { Opcion } from './RepositorioCatalogos';

/** Tipo de sensor: define qué mide y en qué unidad. */
export interface TipoSensor {
  id: number;
  nombre: string;
  /** Símbolo que acompaña al valor en pantalla: °C, %, V… */
  unidad: string | null;
  /** Nombre completo de la unidad: «Grados Celsius». */
  unidadNombre: string | null;
  esDigital: boolean;
  /** Velocidad de muestreo de los analógicos: cierto = rápido. */
  rapido: boolean;
}

/**
 * Lo que un sensor guarda en su tabla y la vista de listado no devuelve.
 * Es lo que hace falta para abrir su formulario de edición con sus valores.
 */
export interface DetalleSensor {
  tipoId: number | null;
  categoria: string | null;
  /** Cuánto historial se dibuja en la gráfica, en segundos. */
  ventanaSegundos: number | null;
  /** Cada cuánto se espera una medición, en segundos. */
  intervaloSegundos: number | null;
  /** El sensor admite que se le ignore mientras arranca el equipo. */
  modoArranque: boolean;
}

/** Tipo de actuador habilitado para un cliente. */
export interface TipoActuadorCliente {
  id: number;
  nombre: string;
  categoria: string | null;
  clienteId: number | null;
  habilitado: boolean;
}

/**
 * Lo que exige `tipos_sensores`. Las cuatro primeras son obligatorias en la
 * base; enviar sólo el símbolo hacía fallar el alta con un error genérico.
 *
 * `tipo` es la velocidad de muestreo de los analógicos (falso = lento,
 * cierto = rápido). En los digitales no aplica y va siempre en falso, igual
 * que hacía la aplicación anterior.
 */
export interface DatosTipoSensor {
  nombre: string;
  unidad_nombre: string;
  unidad_simbolo: string;
  es_digital: boolean;
  tipo: boolean;
}

/**
 * Catálogos de tipos.
 *
 * Los tipos de sensor son globales; los de actuador se habilitan por cliente,
 * porque no todos los clientes tienen contratados los mismos equipos.
 */
export class RepositorioTipos {
  async tiposDeSensor(): Promise<TipoSensor[]> {
    const { data, error } = await supabase
      .from('tipos_sensores')
      .select('id, nombre, unidad_simbolo, unidad_nombre, es_digital, tipo')
      .order('nombre', { ascending: true });

    if (error) throw traducirErrorSupabase(error);

    return ((data ?? []) as Record<string, unknown>[]).map((fila) => ({
      id: aNumero(fila.id) ?? 0,
      nombre: aTexto(fila.nombre) ?? 'Sin nombre',
      unidad: aTexto(fila.unidad_simbolo),
      unidadNombre: aTexto(fila.unidad_nombre),
      esDigital: aBooleano(fila.es_digital) ?? false,
      rapido: aBooleano(fila.tipo) ?? false,
    }));
  }

  async crearTipoSensor(datos: DatosTipoSensor): Promise<void> {
    const { error } = await supabase.from('tipos_sensores').insert(datos);
    if (error) throw traducirErrorSupabase(error);
  }

  async actualizarTipoSensor(id: number, datos: Partial<DatosTipoSensor>): Promise<void> {
    const { error } = await supabase.from('tipos_sensores').update(datos).eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async eliminarTipoSensor(id: number): Promise<void> {
    const { error } = await supabase.from('tipos_sensores').delete().eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  /** Cuántos sensores usan cada tipo, para avisar antes de borrar uno en uso. */
  async usoDeTipos(): Promise<Map<number, number>> {
    const mapa = new Map<number, number>();

    const { data, error } = await supabase.from('sensores').select('tipo_id');
    if (error) throw traducirErrorSupabase(error);

    for (const fila of (data ?? []) as Record<string, unknown>[]) {
      const tipoId = aNumero(fila.tipo_id);
      if (tipoId !== null) mapa.set(tipoId, (mapa.get(tipoId) ?? 0) + 1);
    }

    return mapa;
  }

  async tiposDeActuadorPorCliente(): Promise<TipoActuadorCliente[]> {
    const { data, error } = await ambitoSesion.aplicar(
      supabase
        .from('v2_vw_tipos_actuadores_cliente_ui')
        .select('id, nombre, categoria, cliente_id, habilitado'),
    );

    if (error) throw traducirErrorSupabase(error);

    return ((data ?? []) as Record<string, unknown>[])
      .map((fila) => ({
        id: aNumero(fila.id) ?? 0,
        nombre: aTexto(fila.nombre) ?? 'Sin nombre',
        categoria: aTexto(fila.categoria),
        clienteId: aNumero(fila.cliente_id),
        habilitado: aBooleano(fila.habilitado) ?? false,
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }

  /**
   * Activa o desactiva un tipo de actuador para un cliente.
   * Pasa por una función del servidor porque la relación entre cliente y tipo
   * tiene reglas que no conviene replicar en el navegador.
   */
  async cambiarHabilitadoTipoActuador(
    clienteId: number,
    tipoId: number,
    habilitado: boolean,
  ): Promise<void> {
    const { error } = await supabase.rpc('v2_set_tipo_actuador_enabled', {
      p_cliente_id: clienteId,
      p_tipo_id: tipoId,
      p_enabled: habilitado,
    });

    if (error) throw traducirErrorSupabase(error);
  }

  /**
   * Tipo y categoría de cada sensor.
   *
   * La vista de sensores no expone estos dos campos, así que al editar uno
   * habría que volver a elegirlos a ciegas. Se leen de la tabla para que el
   * formulario abra con lo que el sensor ya tiene configurado.
   */
  async detallesDeSensores(): Promise<Map<number, DetalleSensor>> {
    const mapa = new Map<number, DetalleSensor>();

    /*
     * La vista de sensores no trae nada de esto: ni el tipo, ni la categoría,
     * ni los tiempos de lectura. Sin ellos el formulario de edición abriría
     * con campos vacíos y guardaría encima ceros.
     */
    const { data, error } = await supabase
      .from('sensores')
      .select(
        'id, tipo_id, categoria, ventana_visualizacion, intervalo_medicion, soporta_modo_arranque',
      );
    if (error) throw traducirErrorSupabase(error);

    for (const fila of (data ?? []) as Record<string, unknown>[]) {
      const id = aNumero(fila.id);
      if (id === null) continue;
      mapa.set(id, {
        tipoId: aNumero(fila.tipo_id),
        categoria: aTexto(fila.categoria),
        ventanaSegundos: aNumero(fila.ventana_visualizacion),
        intervaloSegundos: aNumero(fila.intervalo_medicion),
        modoArranque: fila.soporta_modo_arranque === true,
      });
    }

    return mapa;
  }
  /**
   * Tipos de actuador del catálogo global que un cliente todavía no tiene.
   * Es la lista de la que se elige al darle un tipo nuevo.
   */
  async tiposActuadorDisponibles(clienteId: number): Promise<Opcion[]> {
    const { data, error } = await supabase.rpc('v3_list_tipos_actuadores_disponibles', {
      p_cliente_id: clienteId,
    });

    if (error) throw traducirErrorSupabase(error);

    return ((Array.isArray(data) ? data : []) as Record<string, unknown>[])
      .map((fila) => ({
        id: aNumero(fila.id) ?? aNumero(fila.tipo_id) ?? 0,
        nombre: aTexto(fila.nombre) ?? aTexto(fila.codigo) ?? 'Sin nombre',
      }))
      .filter((opcion) => opcion.id !== 0);
  }

  /** Le da a un cliente un tipo de actuador del catálogo. */
  async agregarTipoActuadorACliente(
    clienteId: number,
    tipoId: number,
    habilitado: boolean,
  ): Promise<void> {
    const { error } = await supabase.rpc('v2_add_tipo_actuador_to_cliente', {
      p_cliente_id: clienteId,
      p_tipo_id: tipoId,
      p_enabled: habilitado,
    });

    if (error) throw traducirErrorSupabase(error);
  }

  /**
   * Le quita a un cliente un tipo de actuador.
   *
   * El servidor lo rechaza si hay actuadores usándolo; ese rechazo se traduce
   * aquí porque su mensaje técnico no le dice nada a quien está en la pantalla.
   */
  async quitarTipoActuadorDeCliente(clienteId: number, tipoId: number): Promise<void> {
    const { error } = await supabase.rpc('v2_remove_tipo_actuador_from_cliente', {
      p_cliente_id: clienteId,
      p_tipo_id: tipoId,
    });

    if (error) {
      const texto = `${error.code ?? ''} ${error.message ?? ''}`.toUpperCase();
      if (texto.includes('23503') || texto.includes('EN_USO')) {
        throw new ErrorApp(
          'CONFLICTO',
          'Ese cliente tiene actuadores de este tipo. Elimínalos o cámbialos de tipo antes de quitarlo.',
          error,
        );
      }
      throw traducirErrorSupabase(error);
    }
  }

  /**
   * Esquema de configuración del tipo, con sus valores de fábrica.
   * Es lo que dibuja el formulario de «Configurar» de un actuador.
   */
  async esquemaDeTipoActuador(
    clienteId: number,
    tipoId: number,
  ): Promise<{
    tipoNombre: string;
    campos: CampoConfiguracion[];
    valoresBase: Record<string, unknown>;
  }> {
    const { data, error } = await supabase
      .from('v2_vw_tipos_actuadores_cliente_ui')
      .select('nombre, config_schema, config_defaults, ui_meta')
      .eq('cliente_id', clienteId)
      .eq('id', tipoId)
      .maybeSingle();

    if (error) throw traducirErrorSupabase(error);

    const fila = (data ?? {}) as Record<string, unknown>;

    return {
      tipoNombre: aTexto(fila.nombre) ?? 'Tipo de actuador',
      campos: interpretarEsquema(fila.config_schema, fila.ui_meta),
      valoresBase: interpretarValoresBase(fila.config_defaults),
    };
  }
}

export const repositorioTipos = new RepositorioTipos();
